import { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Loader2, Search, Check, Clock } from "lucide-react";
import UserBadge from "@/components/UserBadge";

/**
 * Modal for the host to invite ANY user as a co-publisher on their live stream.
 *
 * Two panels:
 *   1) "Suggested" — other users who are currently live (fast, no query needed).
 *   2) "Search results" — debounced GET /api/users?q=<prefix> once the host
 *      starts typing. Lets the host invite viewers/creators who aren't live yet.
 */
export default function CoHostInviteModal({ open, onOpenChange, streamId }) {
  const [q, setQ] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invites, setInvites] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const loadSuggested = useCallback(async () => {
    try {
      const { data } = await api.get("/live");
      const out = [];
      const seen = new Set();
      for (const s of data || []) {
        if (!s.host?.id || s.id === streamId) continue;
        if (seen.has(s.host.id)) continue;
        seen.add(s.host.id);
        out.push(s.host);
      }
      setSuggested(out);
    } catch { /* silent */ }
  }, [streamId]);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/cohost/invites`);
      setInvites(data || []);
    } catch { setInvites([]); }
  }, [streamId]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setSearchResults([]);
    loadSuggested();
    loadInvites();
  }, [open, loadSuggested, loadInvites]);

  // Debounced /users?q= search
  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", { params: { q: query, limit: 20 } });
        setSearchResults(Array.isArray(data) ? data : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const statusOf = (userId) => invites.find((i) => i.invitee_id === userId)?.status;

  const invite = async (userId) => {
    setBusyId(userId);
    try {
      await api.post(`/live/${streamId}/cohost/invite`, { user_id: userId });
      toast.success("Invite sent!");
      await loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite.");
    } finally {
      setBusyId(null);
    }
  };

  const showList = useMemo(() => {
    const isSearching = q.trim().length > 0;
    const rows = isSearching ? searchResults : suggested;
    return { isSearching, rows };
  }, [q, searchResults, suggested]);

  const renderRow = (u) => {
    const st = statusOf(u.id);
    return (
      <div key={u.id} className="flex items-center gap-3 bg-black/30 border border-white/5 px-3 py-2">
        <UserBadge
          userRef={u.id || u.handle}
          className="flex items-center gap-3 min-w-0 flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-md text-left"
          aria-label={`View ${u.display_name}'s profile`}
        >
          <Avatar className="w-9 h-9 border border-white/10">
            <AvatarImage src={u.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-xs">
              {u.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{u.display_name || u.handle}</div>
            <div className="text-[11px] text-zinc-500 truncate">{u.display_name}</div>
          </div>
        </UserBadge>
        {st === "accepted" ? (
          <span className="text-xs text-emerald-400 flex items-center gap-1"><Check size={12} /> live</span>
        ) : st === "pending" ? (
          <span className="text-xs text-amber-400 flex items-center gap-1"><Clock size={12} /> pending</span>
        ) : (
          <Button
            type="button"
            onClick={() => invite(u.id)}
            disabled={busyId === u.id}
            className="btn-crimson rounded-none px-3 py-2 text-xs"
            data-testid={`cohost-invite-${u.id}`}
          >
            {busyId === u.id ? <Loader2 size={12} className="animate-spin" /> : "Invite"}
          </Button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-md border-white/10 max-h-[85vh] overflow-y-auto" data-testid="cohost-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Users size={18} className="text-rose-500" /> Invite a co-host
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            Search by handle or name — they'll get a notification and can accept from anywhere on My Panda X.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by handle or name…"
            className="bg-black/40 border-white/10 rounded-none py-5 pl-9"
            data-testid="cohost-search"
            autoFocus
          />
          {searching && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 animate-spin" />
          )}
        </div>

        {!showList.isSearching && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 pt-2">
            {suggested.length > 0 ? "Currently live" : "Start typing to search users"}
          </div>
        )}

        <div className="space-y-2" data-testid="cohost-candidates">
          {showList.rows.length === 0 && (
            <div className="text-xs text-zinc-500 text-center py-6">
              {showList.isSearching
                ? (searching ? "Searching…" : "No matches. Try a different handle.")
                : "No one else is live right now — search above to invite anyone."}
            </div>
          )}
          {showList.rows.map(renderRow)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
